import React, { useState, useEffect } from 'react';
import { Truck, Wrench, ClipboardCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getCurrentAESTDate } from '../utils/dateUtils';
import { VehiclesPage } from './VehiclesPage';
import { VehiclePlantServicingPage } from './VehiclePlantServicingPage';
import { PreStartReportsPage } from './PreStartReportsPage';

type Tab = 'vehicles' | 'servicing' | 'prestart';

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  { id: 'vehicles',  label: 'Vehicles & Trailers',       icon: Truck,           description: 'View and manage all company vehicles and trailers, including registration, NHVR status, inspection records, and kilometre/hour tracking.' },
  { id: 'servicing', label: 'Vehicle & Plant Servicing',  icon: Wrench,          description: 'Log and track scheduled servicing, maintenance history, and upcoming service intervals for vehicles and plant machinery.' },
  { id: 'prestart',  label: 'Pre-Start Reports',          icon: ClipboardCheck,  description: 'Review completed daily pre-start inspection reports submitted by operators before commencing work.' },
];

export const FleetPage: React.FC = () => {
  const [active, setActive] = useState<Tab>('vehicles');
  const [overdueCount, setOverdueCount] = useState(0);
  const [dueSoonCount, setDueSoonCount] = useState(0);

  useEffect(() => {
    const checkVehicleAlerts = async () => {
      try {
        const { data: vehiclesData, error: vehiclesError } = await supabase
          .from('vehicles')
          .select('id, rego_expiry_date, rego_na, nhvr_required');

        if (vehiclesError) return;

        const { data: inspectionsData } = await supabase
          .from('vehicle_inspections')
          .select('vehicle_id, inspection_type, expiry_date')
          .order('expiry_date', { ascending: false });

        const today = getCurrentAESTDate();
        let overdue = 0;
        let dueSoon = 0;

        (vehiclesData || []).forEach(vehicle => {
          if (!vehicle.rego_na && vehicle.rego_expiry_date) {
            const days = Math.ceil((new Date(vehicle.rego_expiry_date).getTime() - today.getTime()) / 86_400_000);
            if (days < 0) overdue++;
            else if (days <= 30) dueSoon++;
          }

          if (vehicle.nhvr_required === true) {
            const latest = (inspectionsData || [])
              .find(i => i.vehicle_id === vehicle.id && i.inspection_type === 'nhvr');
            if (!latest) {
              overdue++;
            } else {
              const days = Math.ceil((new Date(latest.expiry_date).getTime() - today.getTime()) / 86_400_000);
              if (days < 0) overdue++;
              else if (days <= 30) dueSoon++;
            }
          }
        });

        setOverdueCount(overdue);
        setDueSoonCount(dueSoon);
      } catch (error) {
        console.error('Error checking vehicle alerts:', error);
      }
    };

    checkVehicleAlerts();

    const sub = supabase
      .channel('fleet_vehicles')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicles' }, checkVehicleAlerts)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_inspections' }, checkVehicleAlerts)
      .subscribe();

    return () => { supabase.removeChannel(sub); };
  }, []);

  return (
    <div className="space-y-0">
      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 -mx-6 px-6 mb-6">
        <nav className="flex gap-1" aria-label="Fleet sections">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = active === tab.id;
            const showOverdue = tab.id === 'vehicles' && overdueCount > 0;
            const showDueSoon = tab.id === 'vehicles' && dueSoonCount > 0;
            return (
              <div key={tab.id} className="relative group">
                <button
                  onClick={() => setActive(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {(showOverdue || showDueSoon) && (
                    <span className="inline-flex items-center gap-0.5">
                      {showOverdue && (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
                          {overdueCount > 99 ? '99+' : overdueCount}
                        </span>
                      )}
                      {showDueSoon && (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-amber-500 rounded-full">
                          {dueSoonCount > 99 ? '99+' : dueSoonCount}
                        </span>
                      )}
                    </span>
                  )}
                </button>
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 hidden group-hover:block">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl w-56 leading-snug text-center">
                    {tab.description}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full border-4 border-transparent border-b-gray-900" />
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Page panels — kept mounted so state is preserved when switching tabs */}
      <div className={active === 'vehicles'  ? '' : 'hidden'}><VehiclesPage /></div>
      <div className={active === 'servicing' ? '' : 'hidden'}><VehiclePlantServicingPage /></div>
      <div className={active === 'prestart'  ? '' : 'hidden'}><PreStartReportsPage /></div>
    </div>
  );
};
